// 표 셀 대각선(사선) 렌더 — `TableCellStyle.diagonal`/`diagonalLine`의 단일 렌더 소스.
//
// 한국 양식은 「해당 없음」 칸이나 머리행/열 교차 칸에 대각선을 긋는 관례가 있다(HWP 셀 대각선
// 슬래시/백슬래시/십자와 같은 집합). 사용자 피드백 #55(2026-08-21, "보고서 빌더 표 셀 안에 대각선").
//
// ── 왜 인라인 SVG인가(배경 그라디언트가 아니라) ──────────────────────────────────
// 1) **호스트 전역 CSS에 의존하지 않는다**: SVG `<line stroke>`는 **콘텐츠**라 어느 앱·어느 인쇄
//    설정에서도 나온다. 배경(gradient)은 브라우저 인쇄에서 「배경 그래픽」 미체크면 빠지는데,
//    web은 `index.css`의 `@media print { * { print-color-adjust: exact !important } }`가 그걸
//    덮어 주지만 **admin(저작 캔버스)에는 그 규칙이 없다**. 즉 배경 방식은 "호스트가 그 규칙을
//    갖고 있는가"에 렌더 정확성이 걸리고, rc는 시각 값을 자기가 소유해야 한다
//    (memory feedback-canvas-shell-owns-visual-ground). PDF는 `pageElement.screenshot()` 경로라
//    (server pdf.processor) 배경도 찍히므로 PDF만 보면 두 방식이 같다 — 갈리는 것은 브라우저 인쇄다.
// 2) **정확도**: `linear-gradient`로 대각선을 그리면 두께가 `calc(50%±)` 보정에 의존하고 경계가
//    흐리다. SVG는 `x1="0" y1="0" x2="100%" y2="100%"`로 종횡비와 무관하게 정확한 코너-투-코너를
//    그리고 stroke 두께가 셀 크기에 영향받지 않는다.
// 3) **shorthand 충돌 회피**: `cellStyleToCss`가 `background` **shorthand**를 쓰고 renderTableCell이
//    그 뒤에 `backgroundKey`로 한 번 더 덮으므로, 배경 이미지는 두 번 지워지는 순서 함정에 걸린다.
//
// ── 레이아웃·페이지 계획 측정 중립(근거) ────────────────────────────────────────
// 오버레이는 절대배치(부모 td는 `cellStyleToCss`가 `diagonal` 있을 때만 `position:relative`)라
// 셀 크기·행 높이·페이지 분할 측정(pageMeasure)에 영향이 없다. `pointerEvents:'none'`이라 저작
// 캔버스의 셀 선택/드래그도 가리지 않는다. 측정 층이 읽는 것은 둘뿐이므로 근거도 둘이다:
//  (a) **박스 높이**(offsetHeight — pageMeasure의 heightOf 주입점): td에 붙는 것은 position:relative
//      하나이고 오프셋(top/left/right/bottom)이 없어 기하가 변하지 않으며, 오버레이는 absolute라
//      흐름 밖이어서 셀 내용 높이에 기여하지 않는다. position:relative는 자손의 offsetParent를
//      바꾸지만 rc·admin·web에 `offsetTop`/`offsetParent` 사용이 **0건**이다(좌표는 전부
//      getBoundingClientRect 차분 — 뷰포트 기준이라 offsetParent와 무관. admin canvasRect 참조).
//  (b) **`data-composer-*` 속성을 가진 요소**(querySelectorAll): 오버레이는 그중 하나도 갖지 않는다.
//      유일한 속성 `data-composer-cell-diagonal`은 측정 대상 `data-composer-cell`과 **다른 이름**이라
//      속성 셀렉터에 걸리지 않는다(속성 셀렉터는 이름 정확일치. admin의 `[data-composer-cell^="id::"]`도
//      값 접두 일치라 무관).
// 위 둘은 `__tests__/cellDiagonal.test.tsx`의 「페이지 계획·측정 중립」이 못 박는다 — 속성 목록은
// pageMeasure 소스에서 파생해 손나열이 낡지 않게 했다.
//
// ── 인쇄 경로 ────────────────────────────────────────────────────────────────────
// 레포 전체에 `@media print` 블록은 `apps/web/src/index.css` 하나뿐이고, 그것은 숨김 규칙이 아니라
// `print-color-adjust: exact !important`(전 요소) + `.annotation-overlay` SVG를 **드러내는** 규칙이다.
// absolute/SVG를 숨기는 규칙은 없고, Tailwind `print:` variant 사용도 0건, 서버는
// `emulateMediaType('print')`를 쓰지 않는다(PDF는 print 미디어에 진입조차 하지 않는다).
//
// ⚠️ 페인팅 순서: 절대배치 요소는 in-flow 텍스트 **위**에 그려진다(`z-index:-1`은 border-collapse
// 표의 배경/테두리 층과 겹쳐 셀 배경에 가릴 위험이 있어 쓰지 않는다). 즉 대각선은 글자 위를
// 지나는 hairline이고, 이는 HWP 셀 대각선과 같은 결이다(대각선 칸은 통상 빈칸).
//
// rc Tailwind 금지 규약: 시각 값은 전부 인라인 style/SVG 속성이 소유한다.

import type { CSSProperties, ReactNode } from 'react';
import type { TableCellDiagonal, TableCellStyle } from '@safetyhub/shared';

/**
 * 방향만 지정되고 선 스타일도 셀 테두리도 없을 때 쓰는 엔진 기본 선.
 * `templateOps.defaultCellStyle()`의 `border: '1px solid #000'`과 같은 톤(같은 표에서 겉돌지 않게).
 */
export const DEFAULT_DIAGONAL_LINE = '1px solid #000';

/** mm → px (96dpi) — admin `mmToPx`(sampleTemplate)와 같은 환산. 엔진 저장 단위는 px. */
const MM_TO_PX = 96 / 25.4;

/** 선 없음 표기(빈 문자열/none/hidden) — 대각선 선 해석에서 "미지정"과 같이 취급. */
function isNoLine(css: string): boolean {
  return css.trim() === '' || /\b(?:none|hidden)\b/.test(css);
}

/**
 * 대각선에 쓸 선(CSS border 문자열) 결정 — `diagonalLine` → 셀 `border` → 엔진 기본.
 *
 * 방향이 지정되면 **항상 보이는 선이 나온다**(none/hidden은 미지정과 같이 폴백). 저작 UI가
 * "방향을 골랐는데 아무것도 안 그려지는" 거짓 안내를 내지 않게 하는 규약 — 대각선을 없애는
 * 유일한 방법은 방향(`diagonal`)을 비우는 것이다.
 */
export function resolveDiagonalLine(style: TableCellStyle | undefined): string {
  const explicit = style?.diagonalLine;
  if (explicit !== undefined && !isNoLine(explicit)) return explicit;
  const border = style?.border;
  if (border !== undefined && !isNoLine(border)) return border;
  return DEFAULT_DIAGONAL_LINE;
}

/** CSS border 문자열을 SVG stroke 속성으로 옮긴 결과. */
export interface DiagonalStroke {
  /** stroke 색 */
  color: string;
  /** stroke 두께(px, >0) */
  width: number;
  /** stroke-dasharray(solid면 undefined) */
  dash?: string;
  /** CSS 'double' — 호출처가 두 줄을 평행 배치(각 줄 width/3). */
  double: boolean;
}

/**
 * CSS border 문자열('1px solid #000') → SVG stroke 속성.
 *
 * ⚠️ admin `parseBorder`(EditorPage.tsx, 저작 입력 ↔ 문자열 변환)와 **같은 어휘의 거울**이다 —
 * 선 스타일 옵션(solid/dashed/dotted/double)·px/mm 수용·#rgb 확장을 맞춰 둔다. 한쪽에 선
 * 스타일을 추가하면 다른 쪽도 함께 손댈 것(저작 UI가 제공하는 스타일을 엔진이 못 그리면
 * UI가 거짓말이 된다).
 */
export function parseDiagonalStroke(line: string): DiagonalStroke {
  const widthM = /([\d.]+)\s*(px|mm)?/.exec(line);
  const styleM = /\b(solid|dashed|dotted|double)\b/.exec(line);
  const colorM = /#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/.exec(line);
  let color = colorM ? colorM[0] : '#000000';
  if (color.length === 4) {
    color = `#${color
      .slice(1)
      .split('')
      .map((c) => c + c)
      .join('')}`;
  }
  let width = 1;
  if (widthM) {
    const n = Number(widthM[1]);
    if (Number.isFinite(n) && n > 0) width = widthM[2] === 'mm' ? n * MM_TO_PX : n;
  }
  const kind = styleM ? styleM[1] : 'solid';
  // CSS 근사: dashed = 3:2, dotted = 1:1(두께 단위). double은 아래에서 두 줄로 나눠 그린다.
  const dash =
    kind === 'dashed' ? `${width * 3} ${width * 2}` : kind === 'dotted' ? `${width} ${width}` : undefined;
  return { color, width, dash, double: kind === 'double' };
}

/** 오버레이 SVG — 부모 td의 패딩 박스를 정확히 덮고 레이아웃·입력에 관여하지 않는다. */
const DIAGONAL_SVG_STYLE: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  display: 'block',
  overflow: 'visible',
};

/** 방향별 끝점(퍼센트 좌표 — 종횡비 무관 코너-투-코너). */
const ENDPOINTS: Record<'tlbr' | 'trbl', { x1: string; y1: string; x2: string; y2: string }> = {
  tlbr: { x1: '0', y1: '0', x2: '100%', y2: '100%' },
  trbl: { x1: '100%', y1: '0', x2: '0', y2: '100%' },
};

/**
 * 한 방향의 선 노드 — 보통 `<line>` 하나, 'double'이면 평행 두 줄.
 *
 * double의 평행 이동은 선에 수직인 방향을 대각 성분(±d, ∓d)으로 **근사**한다(퍼센트 좌표라
 * 렌더 시점 셀 종횡비를 모른다). 실제 간격은 d ~ d·√2 사이에서 변하지만 두 줄이 겹치거나
 * 벌어져 보이는 구간이 없다(수평 이동만 쓰면 납작한 셀에서 두 줄이 겹친다).
 */
function diagonalLineNodes(dir: 'tlbr' | 'trbl', stroke: DiagonalStroke): ReactNode {
  const p = ENDPOINTS[dir];
  const common = {
    stroke: stroke.color,
    strokeDasharray: stroke.dash,
    shapeRendering: 'geometricPrecision' as const,
  };
  if (!stroke.double) {
    return <line key={dir} {...p} {...common} strokeWidth={stroke.width} />;
  }
  const d = stroke.width / 2;
  // tlbr(↘)의 수직 근사는 (d,−d), trbl(↗)은 (d,d).
  const dy = dir === 'tlbr' ? -d : d;
  return (
    <g key={dir}>
      <g transform={`translate(${d} ${dy})`}>
        <line {...p} {...common} strokeWidth={stroke.width / 3} />
      </g>
      <g transform={`translate(${-d} ${-dy})`}>
        <line {...p} {...common} strokeWidth={stroke.width / 3} />
      </g>
    </g>
  );
}

/**
 * 셀 대각선 오버레이 노드 — `style.diagonal`이 없으면 null(회귀 0).
 *
 * 양식 탭 표 셀(`renderTableCell`)과 작성폼 표 고정 셀(`composerWidgetTreeRender`) **양쪽**이
 * 이 함수를 호출한다. 부모 td의 `position:relative`는 `cellStyleToCss`가 같은 조건으로 넣는다
 * (두 렌더 사이트 공유) — 한쪽만 고치면 캔버스↔실폼↔인쇄가 갈린다.
 */
export function cellDiagonalOverlay(style: TableCellStyle | undefined): ReactNode | null {
  const dir: TableCellDiagonal | undefined = style?.diagonal;
  if (dir !== 'tlbr' && dir !== 'trbl' && dir !== 'both') return null;
  const stroke = parseDiagonalStroke(resolveDiagonalLine(style));
  return (
    <svg aria-hidden="true" data-composer-cell-diagonal={dir} style={DIAGONAL_SVG_STYLE}>
      {dir !== 'trbl' ? diagonalLineNodes('tlbr', stroke) : null}
      {dir !== 'tlbr' ? diagonalLineNodes('trbl', stroke) : null}
    </svg>
  );
}
